import { SHARED_EXTERNAL_ID } from "@/pages/frontend/chat/adapters/api-thread-list-adapter";
import { useOwnedConversations } from "@/hooks/useOwnedConversations";
import { cachedConversationDetailOptions } from "@/hooks/useConversationDetail";
import { useAuiState } from "@assistant-ui/react";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

export interface ThreadPartition {
    pinnedIndices: number[];
    sharedIndices: number[];
    ownedIndices: number[];
}

type Bucket = "S" | "N" | "O";

interface Segment {
    bucket: Bucket;
    remoteId: string;
}

const segmentOf = (bucket: Bucket, remoteId: string | undefined) =>
    `${bucket}${remoteId ?? ""}`;

const parseSegment = (segment: string): Segment => ({
    bucket: segment[0] as Bucket,
    remoteId: segment.slice(1),
});

// `useAuiState` wraps `useSyncExternalStore`, which compares snapshots with
// `Object.is`. Returning a fresh object from the selector triggers an infinite
// render loop, so the selector MUST return a stable primitive. We return a
// per-index signature string of `"<bucket><remoteId>"` segments joined by
// "|" — a leading "S" (shared), "N" (owned, created in this session: no
// remoteId yet, or the externalId `initialize()` sets) or "O" (any other
// owned row) char, then the remoteId — and materialize the index arrays in
// useMemo. Pin state and list order are NOT in the assistant-ui store, so the
// selector never reads React Query; the useMemo below resolves
// pinned-vs-recents and orders both groups by joining each owned row's
// `remoteId` against the owned-conversations cache.
//
// `state.threads.threadItems` is NOT parallel to `state.threads.threadIds`.
// Internally it's derived from `Object.keys(threadData)` which iterates
// numeric-string keys in ascending order regardless of `threadIds` order.
// `ThreadListPrimitive.ItemByIndex` uses `threadIds[index]` to resolve the
// row, so we must bucket by `threadIds` order and look items up by id.
//
// Literal match on externalId, not a truthy check. `initialize()` sets
// externalId to the numeric conversation id for freshly-created owned
// threads, and `fetch()` sets UNRESOLVED_EXTERNAL_ID on a chat it could not
// resolve; both are truthy but never equal SHARED_EXTERNAL_ID, so those
// stay out of the Shared bucket. The adapter stamps the "cs:shared"
// sentinel only on rows it resolved as shared: `list()`'s rows from the
// shared list, and `fetch()`'s collaborator chats.
//
// A row missing from the owned cache, whose cached detail neither shares nor
// pins it, falls to Recents: first when it was created in this session, after
// the cached rows otherwise, each tier kept in `threadIds` order (stable sort).
//
// `threadIds` can hold an id twice (the runtime appends a fetched thread
// without checking the list again). Every segment keeps its index so indices
// stay equal to `threadIds` positions, and only the first one is bucketed.
export function useThreadPartition(): ThreadPartition {
    const signature = useAuiState((state) => {
        // O(n) Map build per state tick, with n bounded by the one page the
        // sidebar loads per list plus the chats created or opened from
        // outside those pages in this session, so it stays cheap on every
        // keystroke into the composer. Promote to a derived store selector
        // if the sidebar ever loads further pages.
        const byId = new Map(
            state.threads.threadItems.map((item) => [item.id, item])
        );
        return state.threads.threadIds
            .map((id) => {
                const item = byId.get(id);
                const bucket: Bucket =
                    item?.externalId === SHARED_EXTERNAL_ID
                        ? "S"
                        : !item?.remoteId || item.externalId === item.remoteId
                          ? "N"
                          : "O";
                return segmentOf(bucket, item?.remoteId);
            })
            .join("|");
    });

    const owned = useOwnedConversations();
    const segments = useMemo(
        () => (signature ? signature.split("|").map(parseSegment) : []),
        [signature]
    );
    const offPageIds = useMemo(() => {
        const listed = new Set(owned?.items.map((row) => row.id.toString()));
        return [
            ...new Set(
                segments
                    .filter(({ bucket }) => bucket !== "S")
                    .map(({ remoteId }) => remoteId)
                    .filter((id) => id && !listed.has(id))
            ),
        ];
    }, [segments, owned]);
    const details = useQueries({
        queries: offPageIds.map((id) => cachedConversationDetailOptions(id)),
        combine: (results) =>
            results.map(
                ({ data }) =>
                    data && {
                        access_mode: data.access_mode,
                        is_pinned: data.is_pinned,
                    }
            ),
    });

    return useMemo(() => {
        const detailById = new Map(
            offPageIds.map((id, index) => [id, details[index]])
        );
        const pinned = new Set<string>();
        const cachePosition = new Map<string, number>();
        (owned?.items ?? []).forEach((conversation, position) => {
            const id = conversation.id.toString();
            cachePosition.set(id, position);
            if (conversation.is_pinned) pinned.add(id);
        });
        for (const [id, detail] of detailById) {
            if (detail?.access_mode === "owner" && detail.is_pinned) {
                pinned.add(id);
            }
        }

        const pinnedIndices: number[] = [];
        const sharedIndices: number[] = [];
        const ownedIndices: number[] = [];
        const rank: number[] = [];

        const seen = new Set<string>();
        segments.forEach(({ bucket, remoteId }, index) => {
            if (remoteId) {
                if (seen.has(remoteId)) return;
                seen.add(remoteId);
            }
            if (
                bucket === "S" ||
                detailById.get(remoteId)?.access_mode === "collaborator"
            ) {
                sharedIndices.push(index);
                return;
            }
            const isPinned = pinned.has(remoteId);
            rank[index] =
                cachePosition.get(remoteId) ??
                (bucket === "N" && !isPinned ? -1 : cachePosition.size);
            if (isPinned) {
                pinnedIndices.push(index);
            } else {
                ownedIndices.push(index);
            }
        });

        const byRank = (a: number, b: number) => rank[a] - rank[b];
        pinnedIndices.sort(byRank);
        ownedIndices.sort(byRank);

        return { pinnedIndices, sharedIndices, ownedIndices };
    }, [segments, owned, offPageIds, details]);
}
